import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faUtensils, faMugHot, faMartiniGlass, faArrowRight, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import '../styles.css';
import { useShopInfo } from '../settings/SettingsContext';

interface HeaderProps {
  isLogoVisible: boolean;
  selectedLanguage?: 'gr' | 'en';
}

const promoContent = {
  gr: {
    title: 'MenuLynx QR Menu',
    hook: 'Αυτό το ψηφιακό μενού που μόλις χρησιμοποιήσατε; Φτιάχτηκε με το MenuLynx.',
    bullets: [
      'Δημιουργήστε και ενημερώστε το μενού σας σε λεπτά',
      'Χωρίς εκτυπώσεις, χωρίς κόστος αλλαγών',
      'Οι πελάτες σας σκανάρουν και παραγγέλνουν αμέσως',
    ],
    fits: 'Ιδανικό για εστιατόρια, καφετέριες, bar και κάθε χώρο εστίασης.',
    cta: 'Ξεκινήστε δωρεάν δοκιμή',
    ctaLink: 'https://menulynx.com/el/signup-for-trial/',
  },
  en: {
    title: 'MenuLynx QR Menu',
    hook: 'The digital menu you just used? It was built with MenuLynx.',
    bullets: [
      'Create and update your menu in minutes',
      'No printing costs, no hassle with changes',
      'Customers scan and browse instantly',
    ],
    fits: 'Perfect for restaurants, cafes, bars and any food & beverage venue.',
    cta: 'Start your free trial',
    ctaLink: 'https://menulynx.com/signup-for-trial/',
  },
};

const Header: React.FC<HeaderProps> = ({ isLogoVisible, selectedLanguage = 'gr' }) => {
  const info = useShopInfo();
  const infoText = info.infoText ?? process.env.REACT_APP_INFO_TEXT ?? '';
  const [isPopupVisible, setPopupVisible] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const togglePopup = () => {
    setPopupVisible(prev => !prev);
  };

  useEffect(() => {
    function handleDocumentClick(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setPopupVisible(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, []);

  const promo = promoContent[selectedLanguage];
  // Resolve image sources at runtime (record value, else .env fallback). An explicitly
  // cleared value ('') means the shop removed the image — render nothing rather than a broken image.
  const logoSrc = info.logoUrl ?? process.env.REACT_APP_LOGO_PATH;
  const heroSrc = info.heroUrl ?? process.env.REACT_APP_IMAGE_BACKGROUND;

  return (
    <>
      <div className="background-wrapper" style={{ backgroundImage: heroSrc ? `linear-gradient(to bottom, rgba(255, 255, 255, 0) 85%, rgba(248, 243, 234, 1)), url(${heroSrc})` : 'none' }}>
      </div>
        <button ref={buttonRef} className="info-button" onClick={togglePopup} aria-label="Restaurant information">i</button>
        {isPopupVisible && (
          <div ref={popupRef} className="info-popup">
            {infoText ? (
              <p className="info-popup__text">{infoText}</p>
            ) : (
              <>
                <h2><FontAwesomeIcon icon={faQrcode} style={{ color: '#fff' }} /> {promo.title}</h2>
                <p className="info-popup__hook">{promo.hook}</p>
                <ul className="info-popup__bullets">
                  {promo.bullets.map((bullet, i) => (
                    <li key={i}>
                      <FontAwesomeIcon icon={faCheckCircle} className="info-popup__check" />
                      {bullet}
                    </li>
                  ))}
                </ul>
                <p className="info-popup__fits">
                  {promo.fits.split(/(εστιατόρια|restaurants)/i).map((part, i) => {
                    if (part.toLowerCase() === 'εστιατόρια' || part.toLowerCase() === 'restaurants')
                      return <span key={i}>{part} <FontAwesomeIcon icon={faUtensils} style={{ color: '#e67e22' }} /></span>;
                    if (part.includes('καφετέριες') || part.includes('cafes'))
                      return <span key={i}>{part.split(/(καφετέριες|cafes)/i).map((p2, j) => {
                        if (p2.toLowerCase() === 'καφετέριες' || p2.toLowerCase() === 'cafes')
                          return <span key={j}>{p2} <FontAwesomeIcon icon={faMugHot} style={{ color: '#8B5E3C' }} /></span>;
                        if (p2.includes('bar'))
                          return <span key={j}>{p2.split(/(bar)/i).map((p3, k) => {
                            if (p3.toLowerCase() === 'bar' || p3.toLowerCase() === 'bars')
                              return <span key={k}>{p3} <FontAwesomeIcon icon={faMartiniGlass} style={{ color: '#9b59b6' }} /></span>;
                            return <span key={k}>{p3}</span>;
                          })}</span>;
                        return <span key={j}>{p2}</span>;
                      })}</span>;
                    return <span key={i}>{part}</span>;
                  })}
                </p>
                <a
                  href={promo.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="info-popup__cta"
                >
                  {promo.cta} <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </>
            )}
          </div>
        )}
      <div className="logo-container" style={{ display: isLogoVisible && logoSrc ? 'flex' : 'none' }}>
        <div className="logo">
          {logoSrc && <img src={logoSrc} alt="Logo" className="logo-img" />}
        </div>
      </div>
    </>
  );
};

export default Header;
